import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Volume2, BarChart3, Upload, Mic, MicOff } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import {
  applyRating,
  type FsrsStateJson,
  type SchedulerRating,
  type TranslationRow,
  RATING_TO_INT,
} from "@/lib/vocab/scheduler";
import { gradeProduction, normalize } from "@/lib/vocab/grading";
import { isAsrAvailable, isTtsAvailable, listenOnce, speak } from "@/lib/vocab/speech";
import { formatError } from "@/lib/format-error";

/**
 * /learning/vocabulary (BW18 PR-J, multilingual via HOTFIX-BW19.4 PR-A
 * Bug 8).
 *
 * Loads up to 40 due translations (one per direction), picks a card
 * type based on FSRS state (new + learning → recognition; review →
 * mix), shows one at a time with keyboard shortcuts (1/2/3/4 = ratings,
 * Space = flip). Each translation cycles independently through FSRS.
 */

type VocabRow = TranslationRow & {
  card_id: string;
  source_lang: string;
  source_text: string;
  target_lang: string;
  target_text: string;
  example_sentence: string | null;
  phonetic_ipa: string | null;
};

type CardType = "recognition" | "production" | "listening" | "cloze";

const QUEUE_LIMIT = 40;
const TYPE_ROTATION: CardType[] = ["recognition", "production", "listening", "cloze"];

export function LearningVocabulary() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const queue = useQuery({
    queryKey: ["vocab_queue", userId],
    enabled: Boolean(userId),
    staleTime: 0,
    queryFn: async (): Promise<VocabRow[]> => {
      if (!userId) return [];
      const now = new Date().toISOString();
      type Joined = {
        id: string;
        card_id: string;
        target_lang: string;
        target_text: string;
        example_sentence: string | null;
        phonetic_ipa: string | null;
        due_at: string;
        last_reviewed_at: string | null;
        review_count: number;
        fsrs_state: FsrsStateJson | null;
        vocab_card: {
          source_lang: string;
          source_text: string;
          example_sentence: string | null;
          phonetic_ipa: string | null;
        } | null;
      };
      const { data, error } = await supabase
        .from("vocab_translation")
        .select(
          "id, card_id, target_lang, target_text, example_sentence, phonetic_ipa, due_at, last_reviewed_at, review_count, fsrs_state, vocab_card!inner(source_lang, source_text, example_sentence, phonetic_ipa)",
        )
        .eq("user_id", userId)
        .lte("due_at", now)
        .order("due_at", { ascending: true })
        .limit(QUEUE_LIMIT);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Joined[];
      return rows.map((r) => ({
        id: r.id,
        card_id: r.card_id,
        due_at: r.due_at,
        last_reviewed_at: r.last_reviewed_at,
        review_count: r.review_count,
        fsrs_state: r.fsrs_state,
        target_lang: r.target_lang,
        target_text: r.target_text,
        source_lang: r.vocab_card?.source_lang ?? "",
        source_text: r.vocab_card?.source_text ?? "",
        example_sentence: r.example_sentence ?? r.vocab_card?.example_sentence ?? null,
        phonetic_ipa: r.phonetic_ipa ?? r.vocab_card?.phonetic_ipa ?? null,
      }));
    },
  });

  const cards = queue.data ?? [];
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const startedAtRef = useStartedAt();

  // Reset position when a new queue lands.
  useEffect(() => {
    setIndex(0);
    setRevealed(false);
    setReviewedCount(0);
  }, [queue.data]);

  const card = cards[index];
  const cardType: CardType = useMemo(() => {
    if (!card) return "recognition";
    const state = card.fsrs_state?.state ?? "new";
    if (state === "new" || state === "learning") return "recognition";
    return TYPE_ROTATION[index % TYPE_ROTATION.length];
  }, [card, index]);

  const advance = useCallback(() => {
    setRevealed(false);
    setReviewedCount((n) => n + 1);
    setIndex((i) => i + 1);
    startedAtRef.current = Date.now();
  }, [startedAtRef]);

  const review = useMutation({
    mutationFn: async ({
      rating,
      cardType,
      durationMs,
    }: {
      rating: SchedulerRating;
      cardType: CardType;
      durationMs: number;
    }) => {
      if (!card || !userId) throw new Error("Missing card");
      const patch = applyRating(card, rating);
      const { error: updErr } = await supabase
        .from("vocab_translation")
        .update(patch)
        .eq("id", card.id);
      if (updErr) throw updErr;
      const { error: insErr } = await supabase.from("vocab_review").insert({
        user_id: userId,
        card_id: card.card_id,
        translation_id: card.id,
        rating: RATING_TO_INT[rating],
        card_type: cardType,
        duration_ms: durationMs,
      });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["vocab_due_count"] });
      advance();
    },
    onError: (e) => toast.show(`Could not save review, ${formatError(e)}`),
  });

  const handleRate = useCallback(
    (rating: SchedulerRating) => {
      if (!card) return;
      const durationMs = Date.now() - startedAtRef.current;
      review.mutate({ rating, cardType, durationMs });
    },
    [card, cardType, review, startedAtRef],
  );

  // Keyboard shortcuts: 1-4 ratings, Space to flip recognition cards.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!card) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === " ") {
        e.preventDefault();
        setRevealed((v) => !v);
        return;
      }
      if (cardType === "recognition" && !revealed) return;
      switch (e.key) {
        case "1":
          handleRate("again");
          break;
        case "2":
          handleRate("hard");
          break;
        case "3":
          handleRate("good");
          break;
        case "4":
          handleRate("easy");
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, cardType, revealed, handleRate]);

  if (queue.isLoading) {
    return <Loading />;
  }
  if (cards.length === 0) {
    return <DoneEmpty kind="empty" />;
  }
  if (index >= cards.length) {
    return <DoneEmpty kind="finished" reviewedCount={reviewedCount} totalCount={cards.length} />;
  }

  const progress = ((index + 1) / cards.length) * 100;

  return (
    <article aria-labelledby="vocab-review-heading">
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-3)",
          gap: "var(--space-3)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
          }}
        >
          <Link to="/learning" style={{ color: "var(--fg-3)" }}>
            Learning
          </Link>{" "}
          · Vocabulary
        </p>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <Link
            to="/learning/vocabulary/import"
            aria-label="Import cards"
            style={iconLink()}
            title="Import"
          >
            <Upload size={14} strokeWidth={1.6} />
          </Link>
          <Link
            to="/learning/vocabulary/stats"
            aria-label="Stats"
            style={iconLink()}
            title="Stats"
          >
            <BarChart3 size={14} strokeWidth={1.6} />
          </Link>
        </div>
      </header>

      <div
        style={{
          height: 4,
          width: "100%",
          background: "var(--hair)",
          borderRadius: 2,
          overflow: "hidden",
          marginBottom: "var(--space-4)",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "var(--accent)",
            transition: "width 200ms ease-out",
          }}
        />
      </div>

      <h1 id="vocab-review-heading" style={{ position: "absolute", left: -9999 }}>
        Vocabulary review
      </h1>

      <CardSurface
        card={card}
        cardType={cardType}
        revealed={revealed}
        onReveal={() => setRevealed(true)}
        onRate={handleRate}
        disabled={review.isPending}
      />

      <p
        style={{
          marginTop: "var(--space-4)",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          textAlign: "center",
        }}
      >
        Card {index + 1} of {cards.length} ·{" "}
        {cardType === "recognition"
          ? "Tap or press Space to reveal, then 1-4 to rate"
          : "Type your answer; auto-graded"}
      </p>
    </article>
  );
}

function CardSurface({
  card,
  cardType,
  revealed,
  onReveal,
  onRate,
  disabled,
}: {
  card: VocabRow;
  cardType: CardType;
  revealed: boolean;
  onReveal: () => void;
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
}) {
  if (cardType === "recognition") {
    return (
      <RecognitionCard
        card={card}
        revealed={revealed}
        onReveal={onReveal}
        onRate={onRate}
        disabled={disabled}
      />
    );
  }
  if (cardType === "listening") {
    return <ListeningCard card={card} onRate={onRate} disabled={disabled} />;
  }
  if (cardType === "cloze") {
    return <ClozeCard card={card} onRate={onRate} disabled={disabled} />;
  }
  return <ProductionCard card={card} onRate={onRate} disabled={disabled} />;
}

function RecognitionCard({
  card,
  revealed,
  onReveal,
  onRate,
  disabled,
}: {
  card: VocabRow;
  revealed: boolean;
  onReveal: () => void;
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
}) {
  return (
    <section style={cardWrapperStyle()}>
      <CardLangHeader card={card} type="recognition" />
      <div
        onClick={() => !revealed && onReveal()}
        role={!revealed ? "button" : undefined}
        tabIndex={!revealed ? 0 : -1}
        onKeyDown={(e) => {
          if (!revealed && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onReveal();
          }
        }}
        style={{
          padding: "var(--space-7) var(--space-5)",
          textAlign: "center",
          cursor: !revealed ? "pointer" : "default",
          display: "grid",
          gap: "var(--space-3)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-s)",
            color: "var(--fg)",
          }}
        >
          {card.source_text}
        </p>
        {card.phonetic_ipa && (
          <p className="mono" style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
            {card.phonetic_ipa}
          </p>
        )}
        {revealed && (
          <>
            <p
              style={{
                margin: 0,
                color: "var(--fg-2)",
                fontSize: "var(--type-body-l)",
                paddingTop: "var(--space-3)",
                borderTop: "1px solid var(--hair)",
              }}
            >
              {card.target_text}
            </p>
            {card.example_sentence && (
              <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)", fontStyle: "italic" }}>
                {card.example_sentence}
              </p>
            )}
          </>
        )}
      </div>
      {revealed ? (
        <RatingRow onRate={onRate} disabled={disabled} />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-3)" }}>
          <button type="button" onClick={onReveal} style={primaryButtonStyle()}>
            Reveal · Space
          </button>
        </div>
      )}
    </section>
  );
}

function ProductionCard({
  card,
  onRate,
  disabled,
}: {
  card: VocabRow;
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  const [graded, setGraded] = useState<{ rating: SchedulerRating; correct: boolean } | null>(null);

  function submit() {
    if (!value.trim()) return;
    const rating = gradeProduction(card.source_text, value);
    setGraded({ rating, correct: normalize(card.source_text) === normalize(value) });
  }

  return (
    <section style={cardWrapperStyle()}>
      <CardLangHeader card={card} type="production" />
      <div style={{ padding: "var(--space-5)", display: "grid", gap: "var(--space-3)" }}>
        <p style={{ margin: 0, fontSize: "var(--type-body-l)", color: "var(--fg)" }}>
          {card.target_text}
        </p>
        {card.example_sentence && (
          <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
            {card.example_sentence.replace(card.source_text, "_____")}
          </p>
        )}
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !graded) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={`Type the ${card.source_lang} word`}
          disabled={Boolean(graded)}
          style={inputStyle()}
        />
        {graded && (
          <div
            style={{
              padding: "var(--space-3)",
              background: graded.correct ? "var(--accent-wash)" : "var(--bg-3)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
            }}
          >
            <p style={{ margin: 0, color: "var(--fg)" }}>
              Expected: <span style={{ fontWeight: 500 }}>{card.source_text}</span>
            </p>
            <p style={{ margin: "4px 0 0", color: "var(--fg-2)" }}>
              Auto-graded: <strong>{graded.rating}</strong>
            </p>
          </div>
        )}
      </div>
      {graded ? (
        <RatingRow onRate={onRate} disabled={disabled} prefilled={graded.rating} />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-3)" }}>
          <button type="button" onClick={submit} style={primaryButtonStyle()} disabled={!value.trim()}>
            Check · Enter
          </button>
        </div>
      )}
    </section>
  );
}

function ListeningCard({
  card,
  onRate,
  disabled,
}: {
  card: VocabRow;
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  const [graded, setGraded] = useState<SchedulerRating | null>(null);
  const [listening, setListening] = useState(false);
  const ttsAvailable = isTtsAvailable();
  const asrAvailable = isAsrAvailable();

  useEffect(() => {
    if (ttsAvailable) speak(card.source_text, card.source_lang);
  }, [card, ttsAvailable]);

  function submit() {
    if (!value.trim()) return;
    const rating = gradeProduction(card.source_text, value);
    setGraded(rating);
  }

  function startListen() {
    if (!asrAvailable) return;
    setListening(true);
    listenOnce(
      card.source_lang,
      (text) => {
        setValue(text);
        setListening(false);
      },
      () => setListening(false),
    );
  }

  return (
    <section style={cardWrapperStyle()}>
      <CardLangHeader card={card} type="listening" />
      <div style={{ padding: "var(--space-5)", display: "grid", gap: "var(--space-3)" }}>
        <button
          type="button"
          onClick={() => speak(card.source_text, card.source_lang)}
          disabled={!ttsAvailable}
          style={{
            ...primaryButtonStyle(),
            justifySelf: "center",
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--space-2)",
          }}
        >
          <Volume2 size={14} strokeWidth={1.6} /> Play again
        </button>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)", textAlign: "center" }}>
          Type what you heard.
        </p>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !graded) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={Boolean(graded)}
            placeholder="Type or tap mic"
            style={{ ...inputStyle(), flex: 1, minWidth: 0 }}
          />
          {asrAvailable && (
            <button
              type="button"
              onClick={startListen}
              disabled={listening || Boolean(graded)}
              style={iconButtonStyle()}
              aria-label="Voice input"
            >
              {listening ? <MicOff size={14} strokeWidth={1.6} /> : <Mic size={14} strokeWidth={1.6} />}
            </button>
          )}
        </div>
        {graded && (
          <div
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-3)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
            }}
          >
            <p style={{ margin: 0, color: "var(--fg)" }}>
              Expected: <span style={{ fontWeight: 500 }}>{card.source_text}</span>
            </p>
          </div>
        )}
      </div>
      {graded ? (
        <RatingRow onRate={onRate} disabled={disabled} prefilled={graded} />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-3)" }}>
          <button type="button" onClick={submit} style={primaryButtonStyle()} disabled={!value.trim()}>
            Check
          </button>
        </div>
      )}
    </section>
  );
}

function ClozeCard({
  card,
  onRate,
  disabled,
}: {
  card: VocabRow;
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
}) {
  const example = card.example_sentence ?? `${card.source_text} ?`;
  const blanked = example.replace(
    new RegExp(card.source_text, "i"),
    "_____",
  );
  const [value, setValue] = useState("");
  const [graded, setGraded] = useState<SchedulerRating | null>(null);

  function submit() {
    if (!value.trim()) return;
    setGraded(gradeProduction(card.source_text, value));
  }

  return (
    <section style={cardWrapperStyle()}>
      <CardLangHeader card={card} type="cloze" />
      <div style={{ padding: "var(--space-5)", display: "grid", gap: "var(--space-3)" }}>
        <p style={{ margin: 0, color: "var(--fg)", fontSize: "var(--type-body-l)" }}>{blanked}</p>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          {card.target_text}
        </p>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !graded) {
              e.preventDefault();
              submit();
            }
          }}
          disabled={Boolean(graded)}
          placeholder="Fill in the blank"
          style={inputStyle()}
        />
        {graded && (
          <div
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-3)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
            }}
          >
            <p style={{ margin: 0, color: "var(--fg)" }}>
              Answer: <span style={{ fontWeight: 500 }}>{card.source_text}</span>
            </p>
          </div>
        )}
      </div>
      {graded ? (
        <RatingRow onRate={onRate} disabled={disabled} prefilled={graded} />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-3)" }}>
          <button type="button" onClick={submit} style={primaryButtonStyle()} disabled={!value.trim()}>
            Check · Enter
          </button>
        </div>
      )}
    </section>
  );
}

function CardLangHeader({
  card,
  type,
}: {
  card: VocabRow;
  type: CardType;
}) {
  const ttsAvailable = isTtsAvailable();
  const labels: Record<CardType, string> = {
    recognition: "Recognition",
    production: "Production",
    listening: "Listening",
    cloze: "Cloze",
  };
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        borderBottom: "1px solid var(--hair)",
        color: "var(--fg-3)",
        fontSize: "var(--type-meta)",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      <span>
        {labels[type]} · {card.source_lang.toUpperCase()} → {card.target_lang.toUpperCase()}
      </span>
      {ttsAvailable && type !== "listening" && (
        <button
          type="button"
          onClick={() => speak(card.source_text, card.source_lang)}
          aria-label="Hear pronunciation"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--fg-2)",
            cursor: "pointer",
            padding: 4,
          }}
        >
          <Volume2 size={14} strokeWidth={1.6} />
        </button>
      )}
    </header>
  );
}

function RatingRow({
  onRate,
  disabled,
  prefilled,
}: {
  onRate: (r: SchedulerRating) => void;
  disabled: boolean;
  prefilled?: SchedulerRating;
}) {
  const buttons: Array<{ key: SchedulerRating; label: string; subtle: string }> = [
    { key: "again", label: "Again", subtle: "1" },
    { key: "hard", label: "Hard", subtle: "2" },
    { key: "good", label: "Good", subtle: "3" },
    { key: "easy", label: "Easy", subtle: "4" },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "var(--space-2)",
        padding: "var(--space-3)",
        borderTop: "1px solid var(--hair)",
      }}
    >
      {buttons.map((b) => {
        const accent = b.key === prefilled;
        return (
          <button
            key={b.key}
            type="button"
            onClick={() => onRate(b.key)}
            disabled={disabled}
            style={{
              padding: "10px 8px",
              background: accent ? "var(--accent-wash)" : "var(--bg-3)",
              border: `1px solid ${accent ? "var(--accent-dim)" : "var(--hair-2)"}`,
              borderRadius: "var(--radius-card)",
              color: "var(--fg)",
              fontSize: "var(--type-body-s)",
              cursor: disabled ? "not-allowed" : "pointer",
              display: "grid",
              gap: 2,
            }}
          >
            <span style={{ fontWeight: 500 }}>{b.label}</span>
            <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
              {b.subtle}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Loading() {
  return (
    <article>
      <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading queue…</p>
    </article>
  );
}

function DoneEmpty({
  kind,
  reviewedCount,
  totalCount,
}: {
  kind: "empty" | "finished";
  reviewedCount?: number;
  totalCount?: number;
}) {
  return (
    <article aria-labelledby="vocab-done-heading">
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        <Link to="/learning" style={{ color: "var(--fg-3)" }}>
          Learning
        </Link>{" "}
        · Vocabulary
      </p>
      <h1
        id="vocab-done-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        {kind === "empty" ? "Nothing due." : "Done for today."}
      </h1>
      <p style={{ color: "var(--fg-2)", fontSize: "var(--type-body)", marginBottom: "var(--space-4)" }}>
        {kind === "empty"
          ? "Import a CSV to start, or check back tomorrow when new cards mature."
          : `${reviewedCount} of ${totalCount} cards reviewed. The next batch matures based on FSRS scheduling.`}
      </p>
      <div style={{ display: "flex", gap: "var(--space-3)" }}>
        <Link to="/learning/vocabulary/import" style={primaryButtonStyle()}>
          Import cards
        </Link>
        <Link to="/learning/vocabulary/stats" style={{ ...primaryButtonStyle(), background: "transparent", color: "var(--fg)", border: "1px solid var(--hair-2)" }}>
          View stats
        </Link>
      </div>
    </article>
  );
}

function useStartedAt() {
  const ref = useMemo(() => ({ current: Date.now() }), []);
  useEffect(() => {
    ref.current = Date.now();
  }, [ref]);
  return ref;
}

function cardWrapperStyle(): React.CSSProperties {
  return {
    background: "var(--bg-2)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-surface)",
    overflow: "hidden",
  };
}

function primaryButtonStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 38,
    padding: "0 var(--space-4)",
    background: "var(--accent)",
    color: "#0a0b0d",
    border: "none",
    borderRadius: "var(--radius-card)",
    fontSize: "var(--type-body-s)",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "none",
  };
}

function inputStyle(): React.CSSProperties {
  return {
    height: 38,
    padding: "0 var(--space-3)",
    background: "var(--bg-3)",
    color: "var(--fg)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--type-body)",
  };
}

function iconButtonStyle(): React.CSSProperties {
  return {
    width: 38,
    height: 38,
    padding: 0,
    background: "var(--bg-3)",
    color: "var(--fg-2)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

function iconLink(): React.CSSProperties {
  return {
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg-3)",
    color: "var(--fg-2)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    textDecoration: "none",
  };
}
